import React, { useEffect, useState, useRef, useCallback,useContext } from 'react';
import TaskChatService from '../services/TaskChatService';
import { getTaskChatById } from '../services/taskService';
import { FiSend, FiUser, FiX } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next'; 
import { UserContext } from '../Contexts/UserContext';
import { formatDistanceToNow } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { getUsersInfoByIds} from "../services/usersService";

const TaskChat = ({ close, task }) => {

  const {t, i18n} = useTranslation();
  const {user} = useContext(UserContext);
  const [messages, setMessages] = useState([]); // messages in descending order (newest first)
  const [newMessage, setNewMessage] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);  
  const [usersInfo, setUsersInfo] = useState({});
  const currentLang = i18n.language; 
  const locales = {
    en: enUS,
    fr: fr,
  };
   
  const getsenderById = async (id)=>{
    getUsersInfoByIds([id])
      .then((res) => {
        return (res);
      })
      .catch((err) => console.log(err));
  }
  useEffect(() => {
    const fetchChatAndUsers = async () => {
      if (!task?.task?.id) return;
  
      try {
        const data = await getTaskChatById(task.task.id, 0, 9);
        setMessages(data.content || []);
        setPage(0);
        setHasMore(!data.last);
  
        const senderIds = [...new Set(data.content.map(msg => msg.senderId))];
        if (senderIds.length > 0) {
          const userDetails = await getUsersInfoByIds(senderIds);
          const userMap = userDetails.reduce((acc, user) => {
            acc[user.id] = user;
            return acc;
          }, {});
          setUsersInfo(userMap);
        }
      } catch (err) {
        console.error('Error fetching chat history or user info:', err);
      }
    };
  
    fetchChatAndUsers();
  }, [task?.task?.id]);
  
  const loadMoreMessages = useCallback(() => {
    if (task?.task?.id && hasMore) {
      const nextPage = page + 1;
      getTaskChatById(task.task.id, nextPage, 9)
        .then((data) => {
          console.log('Loaded more messages:', data); 
          setMessages((prev) => [...prev, ...data.content]);
          setPage(nextPage);
          if (data.last) {
            setHasMore(false);
          }
        })
        .catch((err) => console.error('Error loading more messages:', err));
    }
  }, [task?.task?.id, hasMore, page]);
 
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          console.log('Sentinel visible (scrolled up), loading more messages...');
          loadMoreMessages();
        }
      },
      { threshold: 1.0 }
    );
    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [loaderRef, hasMore, loadMoreMessages]);
 
  useEffect(() => {
    if (task?.task?.id) {
      TaskChatService.connect(
        () => {
          // console.log('WebSocket connected');
          TaskChatService.subscribeToChat(task.task.id, handleNewMessage);
        },
        (error) => {
          console.error('Connection error:', error);
          if (error.toLowerCase().includes('unauthorized')) {
            window.location.href = '/login';
          }
        }
      );
    }
    return () => TaskChatService.disconnect();
  }, [task?.task?.id]);
 
  const handleNewMessage = (message) => {
    console.log('New WebSocket message received:', message);
    setMessages((prev) => [message, ...prev]);
  }; 
  const handleSend = (e) => {
    e.preventDefault();
    if (newMessage.trim() && task?.task?.id) {
      TaskChatService.sendMessage(task.task.id, newMessage.trim());
      setNewMessage('');
    }
  };

  return (
    <div className='fixed top-0 left-0 h-[100vh] w-[100vw] z-50'>
      <motion.div
        initial={{ right: '-20rem' }}
        animate={{ right: '1rem' }}
        exit={{ right: '-20rem' }}
        transition={{ type: 'spring', stiffness: 100, duration: 500 }}
        className='absolute top-[1%] h-[98%] w-80 bg-white z-50 p-3 customShadow rounded-xl'
      >
        <div className='flex flex-col justify-between h-full'> 
          <div className='flex items-center justify-between'>
            <h2 className='font-semibold'>{t('COMMON.TASK_TYPE.TASK_CHAT')}</h2>
            <FiX className='cursor-pointer' onClick={close} />
          </div>  
          <div className='flex flex-col-reverse gap-3 overflow-y-auto max-h-[80vh] p-2'>
          {messages.map((msg, i) => {
            const isMine = msg.senderId === user.id;
            const sender = usersInfo[msg.senderId];   
            return (
              <div key={i} className={isMine ? 'flex flex-col items-end' : ''}>
                <div className='flex items-center gap-2 max-w-full'>
                  {!isMine && (
                    <div className='border p-2 rounded-full'>
                      <FiUser size={16} />
                    </div>
                  )}
                  <div
                    className={`border px-4 py-1.5 w-max text-sm rounded-b-lg ${
                      isMine ? 'rounded-l-lg bg-purple-light text-white' : 'rounded-r-lg'
                    }`}
                  >
                    {msg.content}
                  </div>
                  {isMine && (
                    <div className='border p-2 rounded-full'>
                      <FiUser size={16} />
                    </div>
                  )}
                </div> 
                <p className={`text-gray-500 text-xs mt-1 ${isMine ? 'mr-12' : 'ml-12'}`}>
                  {formatDistanceToNow(new Date(msg.timestamp), { addSuffix: true, locale: locales[currentLang] || enUS })} 
                  • {sender ? `${sender.firstName} ${sender.lastName}` : 'Unknown'}
                </p>
              </div>
            );
          })} 
            <div ref={loaderRef} className='h-2' />
          </div> 
          <form className='w-full flex items-center justify-between gap-3 mt-4' onSubmit={handleSend}>
            <input
              className='border rounded-full p-2 px-3 text-sm w-full'
              placeholder='Type a message...'
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <button
              type='submit'
              className={`border p-2 rounded-full cursor-pointer ${
                newMessage.trim() ? 'bg-purple-light text-white hover:bg-purple-dark' : 'text-gray-500'
              }`}
              disabled={!newMessage.trim()}
            >
              <FiSend />
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default TaskChat;


 